import * as PIXI from 'pixi.js'

/**
 * This plugin ensures that if a video is loaded via the loader, then a texture is automatically populated in the result
 */
const VideoTextureLoaderPlugin: PIXI.ILoaderPlugin = {
  async use (resource: PIXI.LoaderResource, next: () => void): Promise<void> {
    try {
      if (resource.data instanceof HTMLVideoElement) {
        // Mute and loop if the loaded URL is a video
        resource.data.muted = true
        resource.data.loop = true
        resource.texture = PIXI.Texture.from(resource.data)

      } else if (resource.loadType === PIXI.LoaderResource.LOAD_TYPE.XHR && resource.extension !== 'gif') {
        const blob = resource.data
        if (blob instanceof Blob && blob.type.startsWith('video')) {
          await loadVideoResource(resource)
        }
      }
    } catch (e) {
      resource.error = e as Error
      resource.complete()
    } finally {
      next()
    }
  },
  pre (resource: PIXI.LoaderResource, next: () => void): void {
    // Required as extension for the progressive_redirect is not actually an extension, and the AnimatedGifLoader sets the
    // xhrType for gifs to be 'buffer' and the loadtype to be 'xhr', so need to avoid those resources with this boolean
    if (resource.loadType === PIXI.LoaderResource.LOAD_TYPE.XHR && resource.extension !== 'gif') {
      resource.xhrType = 'blob'
    }
    next()
  }
} 

const loadVideoResource = (resource: PIXI.LoaderResource): Promise<void> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video')
    video.src = resource.url
    video.muted = true
    video.loop = true
    video.crossOrigin = 'anonymous'

    //Listening to errors while loading video resources. Eg: Uploading a video with a wrong extension.
    video.addEventListener('error', (errorEvent: ErrorEvent) => {
      video.remove()
      reject(errorEvent.error)
    })

    video.addEventListener('loadeddata', async () => {
      const videoResource = new PIXI.VideoResource(video, { autoLoad: false })
      const loadedVideo = await videoResource.load()
      if (loadedVideo) {
        resource.texture = new PIXI.Texture(new PIXI.BaseTexture(loadedVideo))
      } else {
        resource.error = new Error('Error loading video element')
      }
      video.remove()
      resolve()
    })
  })
}

export default VideoTextureLoaderPlugin